/* eslint-disable react/prop-types */
import ClearIcon from '@mui/icons-material/Clear'
import { Box, Stack, Typography } from '@mui/material'
import Decimal from 'decimal.js'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { CSSProperties, forwardRef, Ref, useCallback, useState } from 'react'
import { FixedSizeList as List, type ListOnItemsRenderedProps } from 'react-window'

import Button from '@/components/base/Button'
import IconNoData from '@/components/base/IconNoData'
import IconSearch from '@/components/base/IconSearch'
import { useBalances } from '@/hooks/use-balances'
import { useRemitanoRate } from '@/hooks/use-settings'
import useTrans from '@/hooks/useTrans'
import { CustomTokenInfo } from '@/utils/helpers'
import { IconName,staticImportIcon } from '@/utils/static-import-icon'

import BaseDialog from '../../BaseDialog'
import { StyledInput } from '../styles'
import TokenListItem from '../TokenListItem'
import { TokenListProps } from '../types'
import { calculateItemPositionTop, handleClickHighlightMessage,listHeight, styling } from './utils'

const Container = ({
  options,
  height,
  maxHeight,
  onClickTokenItem,
  search = '',
  nickName,
  onSearch,
  hasMoreTokens,
  loadMoreTokens,
  onClickClose,
  highlightMessages,
  slot,
}: TokenListProps, ref: Ref<HTMLDivElement>) => {
  const trans = useTrans()
  const router = useRouter()
  const { balances } = useBalances()
  const { data: rateData } = useRemitanoRate()
  const [openWarningDialog, setOpenWarningDialog] = useState(false)
  const [warningToken, setWarningToken] = useState<CustomTokenInfo | null>(null)

  const onSelectToken = useCallback((option: CustomTokenInfo) => {
    if (option.type === 'user' && option.tags && !option.tags.length) {
      setOpenWarningDialog(true)
      setWarningToken(option)
    } else {
      onClickTokenItem(option)
    }
  }, [onClickTokenItem])

  const onClickUnderstand = useCallback(() => {
    setOpenWarningDialog(false)
    if (warningToken) onClickTokenItem(warningToken)
  }, [onClickTokenItem, warningToken])

  const onItemsRendered = useCallback(
    async ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (hasMoreTokens && visibleStopIndex === options.length) {
        await loadMoreTokens()
      }
    },
    [hasMoreTokens, loadMoreTokens, options],
  )

  const childrenOfList = useCallback(({ index, style }: { index: number; style: CSSProperties }) => {
    const opt = options[index]

    if (!opt) return null

    const rateKey = opt.symbol.toUpperCase() as keyof typeof rateData
    const rate = rateData && rateData[rateKey] || 0
    const balance = balances[opt.symbol] || 0
    const balanceUsd = rate ? new Decimal(balance).mul(rate).toNumber() : 0

    return (
      <div
        key={opt.address}
        className="flex w-full items-center justify-center overflow-hidden"
        style={{
          ...style,
          top: calculateItemPositionTop(index),
          height: styling.itemSize,
          marginTop: index ? styling.itemGap : 0,
        }}
      >
        <TokenListItem
          key={opt.symbol}
          option={opt}
          nickName={nickName}
          onClick={onSelectToken}
          balance={balance}
          balanceUsd={balanceUsd}
        />
      </div>
    )
  }, [options, onSelectToken, nickName, rateData, balances])

  const onClickHighlightMessages = useCallback(() => {
    handleClickHighlightMessage(highlightMessages.url, router)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightMessages.url])

  return (
    <>
      {
        highlightMessages.markdown && (
          <Box
            title={highlightMessages.url}
            onClick={onClickHighlightMessages}
            data-testid="highlight-messages"
            className="px-6 py-4 pb-7 relative bottom-[-16px] flex items-start justify-center bg-[#FF6701] rounded-t-2xl gap-2 cursor-pointer underline select-none"
          >
            <Image
              src={staticImportIcon(IconName.REPEAT)}
              alt="repeat"
              width={24}
              className="relative top-[-2px]"
            />
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{ __html: highlightMessages.markdown }}
            />
          </Box>
        )
      }
      <Box className="px-6 py-4 bg-[#141A23] sm:bg-[#1A2330] rounded-t-2xl relative" display="flex" justifyContent="space-between">
        <Typography className="text-xl font-bold">{trans.home.select_a_token}</Typography>
        <ClearIcon className="cursor-pointer" onClick={onClickClose} />
      </Box>
      <Stack ref={ref} className="bg-[#1E2735] px-6 pt-2 pb-6 sm:rounded-b-2xl gap-4">
        <StyledInput
          fullWidth={false}
          disableUnderline
          placeholder={trans.common.token_picker_filter_by}
          startAdornment={<IconSearch />}
          className="w-full"
          value={search}
          onChange={(evt) => onSearch?.(evt.target.value)}
          data-testid="search-coins-input"
        />
        {slot}
        <Stack gap={0.5}>
          <Typography className="text-sm font-bold opacity-[0.72]">RENEC tokens</Typography>
          <Stack gap={0.5} className="overflow-auto" sx={{ maxHeight, height }}>
            <List
              className="token-list"
              height={listHeight.toNumber()}
              width="100%"
              itemCount={options.length + +hasMoreTokens}
              itemSize={listHeight.div(styling.itemPerView).toNumber()}
              onItemsRendered={onItemsRendered}
            >
              {childrenOfList}
            </List>
            {
              options.length === 0 && (
                <Box alignItems="center" className="text-center absolute top-1/2 left-1/2 translate-x-[-50%] translate-y-[-50%]">
                  <IconNoData />
                  <Typography className="text-sm mt-2 text-[#B0B2BE]">{trans.common.token_picker_no_tokens_found}</Typography>
                </Box>
              )
            }
          </Stack>
        </Stack>
      </Stack>
      <BaseDialog
        data-testid="warning-unknown-dialog"
        title={trans.common.warning}
        body={(
          <Stack className="justify-center items-center">
            <Image src="/warning.png" alt="warning" width="95" height="94" className="mb-6"/>
            <Typography className="text-xl font-bold mb-4" data-testid="warning-unknown-token-title">{trans.common.unknown_token}</Typography>
            <Typography className="text-center" data-testid="warning-unknown-token-content">
              {trans.common.warning_unknown_token_content}
            </Typography>
          </Stack>
        )}
        handleClose={() => setOpenWarningDialog(false)}
        isOpened={openWarningDialog}
        isSmallPopup={true}
        button={(
          <Stack display="flex" className="w-full space-y-3">
            <Button
              data-testid="warning-unknown-dialog-understand-btn"
              variant="primary"
              size="lg"
              onClick={onClickUnderstand}
            >
              {trans.common.i_understand}
            </Button>
            <Button
              data-testid="warning-unknown-dialog-cancel-btn"
              variant="outlined"
              size="lg"
              onClick={() => setOpenWarningDialog(false)}
            >
              {trans.common.cancel}
            </Button>
          </Stack>
        )}
        useDrawerOnMobile={true}
      />
    </>
  )
}

export default forwardRef(Container)
